import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useEmployees } from './EmployeesContext';
import localApiAdapter from '../services/localApiAdapter';
import { transformTimeEntry, transformTimeTrackingCode } from '../../shared/utils/timeTrackingService';
import i18n from '../../i18n';

const tt = (key, opts) => i18n.t(`serviceMessages.timeTracking.${key}`, opts);

function normalizeTimeTrackingCodes(list) {
  const arr = Array.isArray(list) ? list : [];
  return arr.filter(Boolean).map((row) => transformTimeTrackingCode(row));
}

function findEmployeeForEntry(employees, entry) {
  if (!entry || !employees?.length) return undefined;
  const key = entry.employeeId ?? entry.employee_id;
  if (key == null || key === '') return undefined;
  const s = String(key);
  return employees.find(
    (emp) =>
      String(emp.id) === s ||
      String(emp.matricule || '') === s ||
      String(emp.employeeId || '') === s
  );
}

const TimeTrackingContext = createContext();

export const useTimeTracking = () => {
  const context = useContext(TimeTrackingContext);
  if (!context) {
    throw new Error(tt('hook_outside_provider'));
  }
  return context;
};

export const TimeTrackingProvider = ({ children }) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeTrackingCodes, setTimeTrackingCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const { employees } = useEmployees();

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const codesData = await localApiAdapter.get('/timetracking/codes');
        setTimeTrackingCodes(normalizeTimeTrackingCodes(codesData));
      } catch (error) {
        console.error('Error fetching time tracking data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const refreshTimeTrackingCodes = async () => {
    try {
      const codesData = await localApiAdapter.get('/timetracking/codes');
      setTimeTrackingCodes(normalizeTimeTrackingCodes(codesData));
    } catch (error) {
      console.error('Error refreshing time tracking codes:', error);
    }
  };

  // Helper function to calculate total hours between two time strings
  const calculateTotalHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return '0:00';
    
    try {
      const checkInTime = new Date(`2000-01-01T${checkIn}`);
      const checkOutTime = new Date(`2000-01-01T${checkOut}`);
      
      if (checkOutTime <= checkInTime) return '0:00';
      
      const diffMs = checkOutTime - checkInTime;
      const diffHrs = Math.floor(diffMs / 1000 / 60 / 60);
      const diffMins = Math.round((diffMs / 1000 / 60) % 60);
      
      return `${diffHrs}:${diffMins < 10 ? '0' : ''}${diffMins}`;
    } catch (error) {
      return '0:00';
    }
  };

  const addTimeEntry = async (entry) => {
    try {
      const employee = findEmployeeForEntry(employees, entry);
      const code = timeTrackingCodes.find(c => c.code === entry.status);
      
      const payload = {
        employee_id: entry.employeeId,
        employee_name: employee ? employee.name : entry.employeeName || tt('unknown_employee'),
        date: entry.date,
        check_in: entry.checkIn,
        check_out: entry.checkOut,
        status: entry.status,
        status_label: code ? code.label : tt('unknown_status'),
        status_color: code ? code.bgColor : 'bg-gray-500',
        notes: entry.notes || '',
        entry_ref: entry.entryRef || entry.entry_ref || null,
      };

      let savedEntry;
      if (entry.id) {
        savedEntry = await localApiAdapter.put(`/timetracking/daily/${entry.id}`, payload);
      } else {
        savedEntry = await localApiAdapter.post('/timetracking/daily', payload);
      }

      const refreshedEntries = await localApiAdapter.get('/timetracking/daily', { date: entry.date });
      const normalized = (Array.isArray(refreshedEntries) ? refreshedEntries : []).map((row) =>
        transformTimeEntry(row)
      );
      setTimeEntries(prev => {
        const filtered = prev.filter(e => e.date !== entry.date);
        return [...filtered, ...normalized];
      });

      return savedEntry;
    } catch (error) {
      console.error('Error saving time entry:', error);
      throw error;
    }
  };

  const deleteTimeEntry = async (entryId) => {
    try {
      await localApiAdapter.delete(`/timetracking/daily/${entryId}`);
      setTimeEntries(prev => prev.filter(entry => entry.id !== entryId));
    } catch (error) {
      console.error('Error deleting time entry:', error);
      throw error;
    }
  };

  const getTimeEntriesForDate = async (date) => {
    try {
      const entries = await localApiAdapter.get('/timetracking/daily', { date });
      const list = Array.isArray(entries) ? entries : [];
      return list.map((row) => transformTimeEntry(row));
    } catch (error) {
      console.error('Error fetching time entries for date:', error);
      return [];
    }
  };

  const getTimeEntriesForMonth = async (year, month) => {
    try {
      const entries = await localApiAdapter.get('/timetracking/monthly', { month, year });
      const list = Array.isArray(entries) ? entries : [];
      return list.filter(Boolean).map((row) => transformTimeEntry(row));
    } catch (error) {
      console.error('Error fetching time entries for month:', error);
      return [];
    }
  };

  const getDailyStats = async (date) => {
    try {
      const stats = await localApiAdapter.get('/timetracking/daily/stats', { date });
      return stats || { present: 0, absent: 0, late: 0, totalHours: 0 };
    } catch (error) {
      console.error('Error fetching daily stats:', error);
      return { present: 0, absent: 0, late: 0, totalHours: 0 };
    }
  };

  const getMonthlyStats = async (year, month) => {
    try {
      const stats = await localApiAdapter.get('/timetracking/monthly/stats', { month, year });
      if (!stats) return { totalDays: 0, presentDays: 0, absentDays: 0 };
      // L’API renvoie presences / absences ; l’UI mensuelle attend presentDays / absentDays
      return {
        ...stats,
        presentDays: stats.presentDays ?? stats.presences ?? 0,
        absentDays: stats.absentDays ?? stats.absences ?? 0
      };
    } catch (error) {
      console.error('Error fetching monthly stats:', error);
      return { totalDays: 0, presentDays: 0, absentDays: 0 };
    }
  };

  const addTimeTrackingCode = async (code) => {
    try {
      const codeStr = String(code.code || '');
      const payload = {
        code: code.code,
        label: code.label,
        color: code.color,
        bg_color: code.bgColor,
        description: code.description || '',
        category: code.category || 'presence',
        type:
          code.type ||
          (codeStr.startsWith('V-') ? 'vehicle' : 
           codeStr.startsWith('E-') ? 'equipment' : 'employee'),
        is_active: code.isActive !== false ? 1 : 0
      };

      const savedCode = await localApiAdapter.post('/timetracking/codes', payload);
      const refreshedCodes = await localApiAdapter.get('/timetracking/codes');
      setTimeTrackingCodes(normalizeTimeTrackingCodes(refreshedCodes));
      return savedCode;
    } catch (error) {
      console.error('Error adding time tracking code:', error);
      throw error;
    }
  };

  const updateTimeTrackingCode = async (codeId, updates) => {
    try {
      const payload = {
        label: updates.label,
        color: updates.color,
        bg_color: updates.bgColor,
        description: updates.description,
        category: updates.category,
        type: updates.type,
        is_active: updates.isActive !== false ? 1 : 0
      };

      await localApiAdapter.put(`/timetracking/codes/${codeId}`, payload);
      const refreshedCodes = await localApiAdapter.get('/timetracking/codes');
      setTimeTrackingCodes(normalizeTimeTrackingCodes(refreshedCodes));
    } catch (error) {
      console.error('Error updating time tracking code:', error);
      throw error;
    }
  };

  const deleteTimeTrackingCode = async (codeId) => {
    try {
      await localApiAdapter.delete(`/timetracking/codes/${codeId}`);
      setTimeTrackingCodes(prev => prev.filter(code => code.id !== codeId));
    } catch (error) {
      console.error('Error deleting time tracking code:', error);
      throw error;
    }
  };

  const generateDailyEntries = async (date) => {
    try {
      const result = await localApiAdapter.post('/timetracking/daily/generate', { date });
      const refreshedEntries = await localApiAdapter.get('/timetracking/daily', { date });
      setTimeEntries(prev => {
        const filtered = prev.filter(e => e.date !== date);
        return [...filtered, ...refreshedEntries];
      });
      return refreshedEntries;
    } catch (error) {
      console.error('Error generating daily entries:', error);
      throw error;
    }
  };

  const value = {
    // Data
    timeEntries,
    timeTrackingCodes,
    employees,
    loading,

    // Time entry methods
    addTimeEntry,
    deleteTimeEntry,
    getTimeEntriesForDate,
    getTimeEntriesForMonth,
    generateDailyEntries,

    // Statistics
    getDailyStats,
    getMonthlyStats,

    // Code management
    addTimeTrackingCode,
    updateTimeTrackingCode,
    deleteTimeTrackingCode,
    refreshTimeTrackingCodes,

    // Utilities
    calculateTotalHours
  };

  return (
    <TimeTrackingContext.Provider value={value}>
      {children}
    </TimeTrackingContext.Provider>
  );
};

export default TimeTrackingContext;

